import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography, Box, IconButton } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import {
    updateAmountLineItem,
    selectAdditionalAmount,
    removeAmountLineItems,
    selectPerksPaymentOptionType,
    selectPerksDuration,
} from '../../features/cart/cartSlice';
import Button from 'client/componentLibrary/Button';
import { currencySign } from 'common/helpers';
import { selectDonationCurrency } from '../../features/checkout/checkoutSlice';
import config from 'client/config';
import { handleIntegerInput } from 'client/helpers/handleIntegerInput';
import { PERKS_PAYMENT_OPTIONS } from 'common/constants/perks';
import { formatDonationAmount } from 'client/helpers/formatDonationAmount';

export const AdditionalAmount = ({
    enableInputs,
    selectedCurrency,
    removeAmountLineItems,
    updateAmountLineItem,
    additionalAmount,
    perksDuration,
    perksPaymentOption,
    classes,
}) => {
    const { formatMessage } = useIntl();
    const [newAdditionalAmount, setNewAdditionalAmount] = useState(null);
    const [inputFocused, setInputFocused] = useState(false);

    const handleAdditionalAmountUpdated = value => {
        updateAmountLineItem(value);
    };

    const handleNewAdditionalAmountUpdated = value => {
        setNewAdditionalAmount(value);
    };

    const handleAdditionalAmountConfirmed = () => {
        updateAmountLineItem(newAdditionalAmount);
        setNewAdditionalAmount(null);
    };

    return (
        <>
            {!additionalAmount && enableInputs && (
                <Box className={classes.cartItem}>
                    <Box
                        className={classNames(
                            classes.cartAmountInput,
                            classes.newAdditionalAmount,
                        )}
                    >
                        <Typography variant="subtitle1">
                            <FormattedMessage
                                id="shoppingCart.additionalAmountDescription"
                                defaultMessage="Optional: Add an extra donation to your cart by entering any amount:"
                            />
                        </Typography>
                        <Box className="inputWrapper">
                            <Box className="inputWithCurrency">
                                <span className="currencySign">
                                    {currencySign(selectedCurrency)}
                                </span>
                                <div className="inputWithSuffix">
                                    <input
                                        value={
                                            newAdditionalAmount
                                                ? newAdditionalAmount.toLocaleString(
                                                      'en-EN',
                                                  )
                                                : ''
                                        }
                                        type="text"
                                        pattern="^\d*(,\d{3})*$"
                                        inputMode="numeric"
                                        name="newAdditionalAmount"
                                        placeholder={
                                            inputFocused
                                                ? ''
                                                : formatMessage({
                                                      id: 'EnterAmountInput.placeholder',
                                                      defaultMessage:
                                                          'Enter Amount...',
                                                  })
                                        }
                                        onChange={handleIntegerInput(
                                            handleNewAdditionalAmountUpdated,
                                        )}
                                        onFocus={() => setInputFocused(true)}
                                        onBlur={() => setInputFocused(false)}
                                    />

                                    {(inputFocused || newAdditionalAmount) && (
                                        <span
                                            className={classNames(
                                                classes.suffix,
                                            )}
                                        >
                                            .00
                                        </span>
                                    )}
                                </div>
                            </Box>
                            <Button
                                className={'addDonationButton'}
                                disabled={!newAdditionalAmount}
                                onClick={handleAdditionalAmountConfirmed}
                            >
                                <FormattedMessage
                                    id="shoppingCart.confirmAdditionalAmount"
                                    defaultMessage="Add Donation"
                                />
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {additionalAmount && (
                <Box className={classes.cartItem}>
                    <figure className={classes.imagePlaceholder}>
                        <img
                            src={`${config.publicHost}/images/additional-amount.png`}
                        />
                    </figure>
                    <Box className={classes.itemDetails}>
                        <Box>
                            <Typography variant={'h6'}>
                                <FormattedMessage
                                    id="shoppingCart.additionalAmount"
                                    defaultMessage="Additional amount"
                                />
                            </Typography>
                            <Typography className={classes.itemPrice}>
                                {formatDonationAmount(
                                    additionalAmount,
                                    selectedCurrency,
                                    {
                                        isRecurring:
                                            perksPaymentOption ===
                                            PERKS_PAYMENT_OPTIONS.RECURRING,
                                        recurringDuration: perksDuration,
                                    },
                                )}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        className={classNames(
                            classes.cartAmountInput,
                            classes.additionalAmountInput,
                        )}
                    >
                        {enableInputs && (
                            <>
                                <span className="currencySign">
                                    {currencySign(selectedCurrency)}
                                </span>
                                <input
                                    value={
                                        additionalAmount
                                            ? additionalAmount.toLocaleString(
                                                  'en-EN',
                                              )
                                            : ''
                                    }
                                    type="text"
                                    inputMode="numeric"
                                    pattern="^\d*(,\d{3})*$"
                                    name="additionalAmount"
                                    onChange={handleIntegerInput(
                                        handleAdditionalAmountUpdated,
                                    )}
                                />
                            </>
                        )}
                        <IconButton
                            onClick={removeAmountLineItems}
                            className={classes.deleteButton}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </>
    );
};

AdditionalAmount.propTypes = {
    additionalAmount: PropTypes.number,
    classes: PropTypes.object,
    enableInputs: PropTypes.bool,
    removeAmountLineItems: PropTypes.func.isRequired,
    selectedCurrency: PropTypes.string,
    updateAmountLineItem: PropTypes.func,
    perksDuration: PropTypes.number,
    perksPaymentOption: PropTypes.string,
};

const mapStateToProps = state => ({
    additionalAmount: selectAdditionalAmount(state),
    selectedCurrency: selectDonationCurrency(state),
    perksDuration: selectPerksDuration(state),
    perksPaymentOption: selectPerksPaymentOptionType(state),
});

const mapDispatchToProps = {
    removeAmountLineItems,
    updateAmountLineItem: amount => {
        if (!amount) {
            return removeAmountLineItems();
        } else {
            return updateAmountLineItem(amount);
        }
    },
};

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalAmount);
